/**
 *
 * @module sessionInfo
 *
 */

import Check from 'check-types';
import Exception from './../exception/entry';

import SessionDeviceInfo from './sessionDeviceInfo';
import SessionProfileInfo from './sessionProfileInfo';
import SessionAccountInfo from './sessionAccountInfo';
import SessionLocation from './sessionLocation';
import SessionEntitlement from './sessionEntitlement';

const InvalidArgumentException = Exception.InvalidArgumentException;

const {
    boolean,
    instanceStrict,
    nonEmptyString,
    nonEmptyObject,
    array
} = Check.assert;

/**
 *
 * @class SessionInfo
 * @desc Represents the session info provided by the service.
 *
 */
export default class SessionInfo {

    /**
     *
     * @param {Object} options
     * @param {String} options.id
     * @param {SDK.Services.Session.SessionAccountInfo} [options.account]
     * @param {SDK.Services.Session.SessionDeviceInfo} options.device
     * @param {SDK.Services.Session.SessionLocation} options.location
     * @param {SDK.Services.Session.SessionProfileInfo} [options.profile]
     * @param {SDK.Services.Session.SessionEntitlement[]} options.entitlements
     * @param {Boolean} options.inSupportedLocation - Denotes whether a user is in a supported country
     *
     */
    constructor(options) {

        const errorMessage = {
            ctor: 'SessionInfo(options)',
            get options() {
                return `${this.ctor} options should be a non-empty Object`;
            },
            get id() {
                return `${this.ctor} options.id should be a String with length`;
            },
            get account() {
                return `${this.ctor} needs SessionAccountInfo instance`;
            },
            get device() {
                return `${this.ctor} needs SessionDeviceInfo instance`;
            },
            get location() {
                return `${this.ctor} needs SessionLocation instance`;
            },
            get profile() {
                return `${this.ctor} needs SessionProfileInfo instance`;
            },
            get entitlements() {
                return `${this.ctor} needs Array of SessionEntitlement instances`;
            },
            get inSupportedLocation() {
                return `${this.ctor} options.inSupportedLocation should be a Boolean`;
            }
        };

        nonEmptyObject(options, errorMessage.options, InvalidArgumentException);

        const { id, account, device, location, profile, entitlements, inSupportedLocation } = options;

        nonEmptyString(id, errorMessage.id, InvalidArgumentException);

        if (Check.assigned(account)) {
            instanceStrict(
                account,
                SessionAccountInfo,
                errorMessage.account,
                InvalidArgumentException
            );
        }

        instanceStrict(
            device,
            SessionDeviceInfo,
            errorMessage.device,
            InvalidArgumentException
        );

        instanceStrict(
            location,
            SessionLocation,
            errorMessage.location,
            InvalidArgumentException
        );

        if (Check.assigned(profile)) {
            instanceStrict(
                profile,
                SessionProfileInfo,
                errorMessage.profile,
                InvalidArgumentException
            );
        }

        array.of.instanceStrict(
            entitlements,
            SessionEntitlement,
            errorMessage.entitlements,
            InvalidArgumentException
        );

        boolean(inSupportedLocation, errorMessage.inSupportedLocation, InvalidArgumentException);

        /**
         *
         * @access public
         * @type {String}
         *
         */
        this.id = id;

        /**
         *
         * @access public
         * @type {SDK.Services.Session.SessionAccountInfo|undefined}
         *
         */
        this.account = account;

        /**
         *
         * @access public
         * @type {SDK.Services.Session.SessionDeviceInfo}
         *
         */
        this.device = device;

        /**
         *
         * @access public
         * @type {SDK.Services.Session.SessionLocation}
         *
         */
        this.location = location;

        /**
         *
         * @access public
         * @type {SDK.Services.Session.SessionProfileInfo|undefined}
         *
         */
        this.profile = profile;

        /**
         *
         * @access public
         * @type {Array<SDK.Services.Session.SessionEntitlement>}
         *
         */
        this.entitlements = entitlements;

        /**
         *
         * @access public
         * @type {Boolean}
         *
         */
        this.inSupportedLocation = inSupportedLocation;

    }

}



// WEBPACK FOOTER //
// ./src/services/session/sessionInfo.js